import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const MapView = ({ tours, onTourSelect, selectedTour }) => {
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // Default to NYC
  const [hoveredTour, setHoveredTour] = useState(null);

  // Mock map markers based on tour locations
  const getMarkerPosition = (index) => {
    const baseLatLng = { lat: 40.7128 + (index * 0.01), lng: -74.0060 + (index * 0.01) };
    return baseLatLng;
  };

  const handleMarkerClick = (tour) => {
    onTourSelect(tour);
  };

  const renderTourPopup = (tour) => {
    if (!tour) return null;

    return (
      <div className="absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg p-4 z-10 max-w-sm">
        <div className="flex space-x-3">
          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={tour?.image}
              alt={tour?.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground text-sm mb-1 truncate">
              {tour?.title}
            </h4>
            <div className="flex items-center text-xs text-muted-foreground mb-2">
              <Icon name="MapPin" size={12} className="mr-1" />
              <span className="truncate">{tour?.location}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="Star" size={12} className="text-yellow-400 fill-current mr-1" />
                <span className="text-xs font-medium">{tour?.rating}</span>
              </div>
              <div className="text-sm font-bold text-primary">
                ${tour?.price}
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-2 mt-3">
          <Button
            variant="outline"
            size="xs"
            onClick={() => onTourSelect(tour)}
            className="flex-1"
          >
            View Details
          </Button>
          <Button
            size="xs"
            className="flex-1"
          >
            Book Now
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative h-full bg-muted rounded-lg overflow-hidden">
      {/* Map Container */}
      <div className="w-full h-full relative">
        {/* Google Maps Iframe */}
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Tours Map"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${mapCenter?.lat},${mapCenter?.lng}&z=12&output=embed`}
          className="border-0"
        />

        {/* Map Overlay with Tour Markers */}
        <div className="absolute inset-0 pointer-events-none">
          {tours?.slice(0, 10)?.map((tour, index) => {
            const position = getMarkerPosition(index);
            return (
              <div
                key={tour?.id}
                className="absolute pointer-events-auto"
                style={{
                  left: `${20 + (index % 5) * 15}%`,
                  top: `${20 + Math.floor(index / 5) * 20}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <button
                  onClick={() => handleMarkerClick(tour)}
                  onMouseEnter={() => setHoveredTour(tour)}
                  onMouseLeave={() => setHoveredTour(null)}
                  className={`relative w-10 h-10 rounded-full shadow-lg transition-all duration-200 hover:scale-110 ${
                    selectedTour?.id === tour?.id 
                      ? 'bg-primary text-white ring-4 ring-primary/30' :'bg-white text-primary hover:bg-primary hover:text-white'
                  }`}
                >
                  <Icon name="MapPin" size={20} className="absolute inset-0 m-auto" />
                  
                  {/* Price Badge */}
                  <div className="absolute -top-2 -right-2 bg-accent text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                    ${tour?.price}
                  </div>
                </button>
                {/* Hover Tooltip */}
                {hoveredTour?.id === tour?.id && (
                  <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                    {tour?.title}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Map Controls */}
        <div className="absolute top-4 right-4 flex flex-col space-y-2">
          <button className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 transition-colors">
            <Icon name="Plus" size={18} />
          </button>
          <button className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 transition-colors">
            <Icon name="Minus" size={18} />
          </button>
          <button className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 transition-colors">
            <Icon name="Locate" size={18} />
          </button>
        </div>

        {/* View Toggle */}
        <div className="absolute top-4 left-4">
          <div className="bg-white rounded-lg shadow-md p-1 flex">
            <button className="px-3 py-1 text-sm bg-primary text-white rounded-md">
              Map
            </button>
            <button className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground">
              Satellite
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-md p-3">
          <h4 className="text-sm font-medium mb-2">Legend</h4>
          <div className="space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span>Available Tours</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-accent rounded-full"></div>
              <span>Selected Tour</span>
            </div>
          </div>
        </div>
      </div>
      {/* Selected Tour Popup */}
      {selectedTour && renderTourPopup(selectedTour)}
      {/* No Tours Message */}
      {tours?.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/90">
          <div className="text-center">
            <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No tours found</h3>
            <p className="text-muted-foreground">Try adjusting your search criteria</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;